'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { UiReviewTabsBase, ReviewTab } from '@/fo/ui/review/tabs/base';

import { Layout, ReviewsList, Sidebar } from '../components';
import { FilterSyncProvider } from '../hooks/FilterSyncContext';
import { Product, ReviewsFilter, ReviewsData } from '../types';

import { styles } from './UiReviewCatalogBase.styles';

const DEFAULT_FILTERS: ReviewsFilter = {
  selectedRating: null,
  selectedTags: []
};

export interface UiReviewCatalogBaseProps {
  reviewsData: ReviewsData;
  product: Product;
  onActionClick?: (tab: ReviewTab) => void;
  filters?: ReviewsFilter;
  onFiltersChange?: (filters: ReviewsFilter) => void;
  onLoadMore?: (tab: ReviewTab) => void;
  onClose?: () => void;
  isReviewsLoading?: boolean;
  reviewsShowLoadMore?: boolean;
  activeTab?: ReviewTab;
}

export function UiReviewCatalogBase({
  reviewsData,
  product,
  onActionClick,
  onLoadMore,
  isReviewsLoading,
  onFiltersChange,
  reviewsShowLoadMore,
  activeTab: initialActiveTab,
  ...props
}: UiReviewCatalogBaseProps) {
  const t = useTranslations();
  const slots = styles();

  const [activeTab, setActiveTab] = useState<ReviewTab>(
    initialActiveTab ?? ReviewTab.Reviews
  );
  const [filters, setFilters] = useState<ReviewsFilter>(
    props.filters ?? DEFAULT_FILTERS
  );

  const handleFiltersChange = (filters: ReviewsFilter) => {
    setFilters(filters);
    onFiltersChange?.(filters);
  };

  const tabs = [
    {
      id: ReviewTab.Reviews,
      title: t('review.tabs.reviews'),
      emptyTitle: t('review.empty.reviews'),
      count: reviewsData.totalCount
    }
  ];

  const main = (
    <ReviewsList
      reviews={reviewsData.items}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      onLoadMore={() => onLoadMore?.(ReviewTab.Reviews)}
      isShowLoadMore={reviewsShowLoadMore}
      isLoading={isReviewsLoading}
    />
  );

  const sidebar = (
    <Sidebar
      reviewsData={reviewsData}
      product={product}
      activeTab={activeTab}
      onActionClick={onActionClick}
      filters={filters}
      onChangeFilters={handleFiltersChange}
    />
  );

  return (
    <FilterSyncProvider>
      <Layout
        stickyHeader={false}
        header={
          <>
            <UiReviewTabsBase
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={setActiveTab}
              titleClassName={slots.title()}
              className={slots.tabs()}
            />
          </>
        }
        sidebar={sidebar}
      >
        {main}
      </Layout>
    </FilterSyncProvider>
  );
}
